/**
 * React Query hooks for live provider tracking.
 *
 * Both follow the polling convention established by `useNotifications.ts`:
 * `refetchInterval` plus `refetchIntervalInBackground: false`, so nothing polls while
 * the tab is hidden.
 */

import { useQuery } from '@tanstack/react-query'
import {
  fetchOrderTrackingFlags,
  fetchServiceLiveLocation,
  type ServiceLiveLocation,
  type ServiceTrackingFlags,
} from '@/api/liveTracking'

/** How often the map pin refreshes. The backend accepts pings far more often than this. */
const LOCATION_POLL_INTERVAL = 15 * 1000

/** How often we re-check whether a tracking window has opened or closed. */
const FLAGS_POLL_INTERVAL = 30 * 1000

const LIVE_LOCATION_KEY = 'service-live-location'
const TRACKING_FLAGS_KEY = 'order-tracking-flags'

/** Order statuses past which no service can still be in progress. */
const TERMINAL_ORDER_STATUSES = new Set(['completed', 'cancelled', 'refunded', 'draft'])

export function isTerminalOrderStatus(status: string | null | undefined): boolean {
  return TERMINAL_ORDER_STATUSES.has((status ?? '').toLowerCase())
}

/**
 * Poll the per-service tracking flags for an order.
 *
 * This is what re-arms the tracking card when a provider starts a visit while the page
 * is already open — the order details query is far too large to poll for that.
 */
export function useOrderTrackingFlags(orderId: string | undefined, enabled = true) {
  const query = useQuery({
    queryKey: [TRACKING_FLAGS_KEY, orderId],
    queryFn: () => fetchOrderTrackingFlags(orderId!),
    enabled: enabled && !!orderId,
    refetchInterval: FLAGS_POLL_INTERVAL,
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 60 * 1000,
    retry: 1,
  })

  return {
    flags: query.data as ServiceTrackingFlags[] | undefined,
    isLoading: query.isLoading,
  }
}

/**
 * Poll one service's last-known provider position.
 *
 * Mount one per actively-tracked service; unmounting the component is what stops its
 * polling, so the caller only has to stop rendering the card when tracking closes.
 */
export function useServiceLiveLocation(serviceId: string | undefined, enabled = true) {
  const query = useQuery({
    queryKey: [LIVE_LOCATION_KEY, serviceId],
    queryFn: () => fetchServiceLiveLocation(serviceId!),
    enabled: enabled && !!serviceId,
    refetchInterval: LOCATION_POLL_INTERVAL,
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 60 * 1000,
    retry: 1,
  })

  return {
    // `null` is a normal result (no ping yet / not permitted), so it is not an error.
    location: (query.data ?? null) as ServiceLiveLocation | null,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
